import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Package, Plus, Minus, ClipboardList, Truck, Info } from "lucide-react";
import { toast } from "sonner";
import { api, STOCK_LABEL, STOCK_STYLE, fmtPrice } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { ProductCard } from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useQuote();

  useEffect(() => {
    setP(null); setErr(false); setQty(1);
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-500" data-testid="product-not-found">Product not found. <Link to="/catalog" className="text-[#1E7CF2] underline">Back to catalog</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-400">Loading…</div>;

  const others = (p.brand?.products || []).filter((x) => x.product_id !== p.product_id).slice(0, 4);
  const onAdd = () => { add(p, qty); toast.success(`${qty} case${qty > 1 ? "s" : ""} of ${p.name} added`, { action: { label: "View list", onClick: () => setOpen(true) } }); };

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8">
        <Link to="/catalog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1E7CF2]" data-testid="back-to-catalog"><ArrowLeft size={14} /> Catalog</Link>
      </div>
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="sr-card overflow-hidden"><img src={p.image} alt={p.name} className="w-full object-cover aspect-[16/10]" /></div>
          <p className="mt-3 text-xs text-slate-400">Image shows the {p.brand_name} product line. Individual SKU packaging may vary.</p>
        </div>
        <div className="lg:col-span-5">
          <Link to={`/brands/${p.brand_slug}`} className="text-[11px] font-bold uppercase tracking-wider text-white px-3 py-1 rounded-full" style={{ background: p.brand_accent || "#0B1F4B" }} data-testid="product-brand-link">{p.brand_name}</Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0B1F4B] leading-tight" data-testid="product-name">{p.name}</h1>
          <p className="mt-2 text-slate-500">{p.brand?.tagline}</p>

          <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {[["Category", p.category], ["Size", p.size], ["Flavor profile", p.flavor_profile], ["Case pack", p.case_pack ? `${p.case_pack} units / case` : "Confirmed at quotation"], ["SKU", p.sku || "On request"]].map(([k, v]) => (
              <div key={k} className="sr-card-tint px-4 py-3"><dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</dt><dd className="font-semibold text-[#0B1F4B] mt-0.5">{v || "—"}</dd></div>
            ))}
            <div className="sr-card-tint px-4 py-3">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Availability</dt>
              <dd className="mt-1"><span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${STOCK_STYLE[p.stock_status]}`} data-testid="product-stock-badge">{STOCK_LABEL[p.stock_status]}</span></dd>
            </div>
          </dl>

          <div className="mt-6 sr-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Wholesale price</span>
              <span className="text-lg font-extrabold text-[#0B1F4B]" data-testid="product-price">
                {p.price_on_request ? <span className="text-[#1E7CF2] uppercase text-sm tracking-wider">Price on request</span> : `${fmtPrice(p.display_price)} / case`}
              </span>
            </div>
            <p className="mt-2 text-xs text-slate-500 flex gap-2"><Info size={14} className="shrink-0 mt-0.5 text-[#1E7CF2]" />{p.settings?.pricing_note}</p>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center border border-slate-300 rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center hover:text-[#1E7CF2]" data-testid="product-qty-minus"><Minus size={14} /></button>
                <input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 text-center font-mono text-sm outline-none" data-testid="product-qty-input" />
                <button onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center hover:text-[#1E7CF2]" data-testid="product-qty-plus"><Plus size={14} /></button>
              </div>
              <span className="text-xs text-slate-500">cases</span>
              <button onClick={onAdd} className="sr-btn-blue flex-1 justify-center" data-testid="product-add-to-quote"><ClipboardList size={16} /> Add to quote</button>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Checkout opens once pricing, stock and shipping are confirmed. Until then, every order starts as a quote request.</p>
          </div>

          <div className="mt-5 text-sm text-slate-600 space-y-2">
            <p className="flex gap-2"><Truck size={16} className="shrink-0 mt-0.5 text-[#1E7CF2]" /> {p.shipping_notes || p.settings?.shipping_note}</p>
            {p.settings?.samples_available && <p className="flex gap-2"><Package size={16} className="shrink-0 mt-0.5 text-[#1E7CF2]" /> Samples available upon request — mention it in your quote message.</p>}
          </div>

          {p.description && <p className="mt-5 text-sm text-slate-600 leading-relaxed">{p.description}</p>}
          {p.tags?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{p.tags.map((t) => <span key={t} className="text-[11px] rounded-full border border-slate-300 px-2.5 py-1 text-slate-600">{t}</span>)}</div>}
        </div>
      </div>

      {p.brand && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-12 border-t border-slate-200">
          <p className="sr-label sr-red-rule">About {p.brand.name}</p>
          <p className="mt-4 max-w-3xl text-slate-600 text-sm leading-relaxed">{p.brand.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">{p.brand.ideal_buyers?.map((b) => <span key={b} className="text-xs rounded-full bg-[#EBF3FE] text-[#1E40AF] px-3 py-1 font-medium">{b}</span>)}</div>
          {others.length > 0 && <>
            <h2 className="mt-10 text-2xl font-extrabold uppercase text-[#0B1F4B]">More from {p.brand.name}</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{others.map((x) => <ProductCard key={x.product_id} p={x} />)}</div>
          </>}
        </section>
      )}
    </div>
  );
}
